import React, { useEffect, useMemo, useState } from "react";
import { Button, Popover, SelectProps } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Icon from "@mdi/react";
import { mdiAirplane } from "@mdi/js";
import { BrandIcon, CalendarIcon, HomeIcon, PinIcon } from "../search-icons";
import ModifySearchSheet from "./ModifySearchSheet";
import PickerList, { PickerGroup, PickerOption, labelForValue } from "./PickerList";
import MonthGrid from "./MonthGrid";
import BrandDropdown from "./BrandDropdown";
import { BrandOption } from "./BrandCards";
import { monthFormat } from "../../../../utils/antd.props";
import type { Country } from "../../../../interfaces/country.interface";
import type { ListingFilterState } from "../../../../utils/listingSearchParams";

interface Props {
  isMobile: boolean;
  filters: ListingFilterState;
  itineraryOptions: PickerOption[];
  cityOptions: PickerOption[];
  exEnds: SelectProps["options"];
  brands: BrandOption[];
  countries: Country[];
  onSearch: (next: ListingFilterState) => void;
}

const POPULAR = ["Japan", "Korea", "China", "Switzerland", "Europe"];

type OpenField = "keyword" | "exEnd" | "date" | "brands" | null;

export default function ListingSearchBar({
  isMobile,
  filters,
  itineraryOptions,
  cityOptions,
  exEnds,
  brands,
  countries,
  onSearch,
}: Props) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<ListingFilterState>(filters);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [open, setOpen] = useState<OpenField>(null);
  const [keywordText, setKeywordText] = useState(filters.keyword);
  const [dateYear, setDateYear] = useState(
    filters.date ? dayjs(filters.date).year() : dayjs().year()
  );

  useEffect(() => {
    setDraft(filters);
    setKeywordText(filters.keyword);
    if (filters.date) setDateYear(dayjs(filters.date).year());
  }, [filters]);

  const destinationGroups = useMemo<PickerGroup[]>(() => {
    const toOption = (country: Country) => ({ value: country.name, label: country.name });
    const groups: PickerGroup[] = [
      { heading: "Popular Destinations", options: countries.filter((c) => POPULAR.includes(c.name)).map(toOption) },
      { heading: "All Destinations", options: countries.filter((c) => !POPULAR.includes(c.name)).map(toOption) },
    ];

    // A city sharing its name with a country is already listed under
    // Destinations and searches the same way, so skip it.
    const countryNames = new Set(countries.map((country) => country.name.toLowerCase()));
    const cities = cityOptions.filter((city) => !countryNames.has(String(city.label).toLowerCase()));

    if (cities.length) groups.push({ heading: "Cities", options: cities });
    if (itineraryOptions.length) groups.push({ heading: "Itineraries", options: itineraryOptions });

    return groups.filter((group) => group.options.length > 0);
  }, [countries, cityOptions, itineraryOptions]);

  const departureGroups = useMemo<PickerGroup[]>(
    () => [{ options: ((exEnds as any[]) || []).map(({ value, label }) => ({ value, label })) }],
    [exEnds]
  );

  const brandLabel = (value: string) => brands.find((brand) => brand.value === value)?.label || value;

  const brandTags = () => {
    if (!draft.brands.length) return null;
    const [first, ...rest] = draft.brands;
    return (
      <span className="v2-search-field__tags">
        <span className="v2-search-field__tag">{brandLabel(first)}</span>
        {rest.length > 0 && <span className="v2-search-field__tag">+ {rest.length} More</span>}
      </span>
    );
  };

  if (isMobile) {
    const where = filters.keyword || "Anywhere";
    const departure =
      filters.type !== "cruise" ? labelForValue(departureGroups, filters.exEnd[0] || "") : "";
    const when = filters.date ? dayjs(filters.date).format(monthFormat) : "Any date";
    const summary = filters.brands.length
      ? filters.brands.length > 1
        ? `${brandLabel(filters.brands[0])} +${filters.brands.length - 1} more`
        : brandLabel(filters.brands[0])
      : "All Brands";

    return (
      <div className="v2-listing__searchbar v2-listing__searchbar--mobile">
        <button type="button" className="v2-listing__back" onClick={() => navigate(-1)} aria-label="Back">
          <img src="/iceholidays-assets/images/icons/chevron-left-thin.svg" alt="" />
        </button>

        <button type="button" className="v2-listing__pill" onClick={() => setSheetOpen(true)}>
          <img className="v2-listing__pill-icon" src="/iceholidays-assets/images/icons/search-red.svg" alt="" />
          <span className="v2-listing__pill-text">
            <span className="v2-listing__pill-primary">
              {where}
              {departure && ` → ${departure}`}
            </span>
            <span className="v2-listing__pill-secondary">
              {when} · {summary}
            </span>
          </span>
        </button>

        <ModifySearchSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          filters={filters}
          destinationGroups={destinationGroups}
          departureGroups={departureGroups}
          brands={brands}
          onSearch={onSearch}
        />
      </div>
    );
  }

  const dropdown = (field: OpenField, content: React.ReactNode, children: React.ReactNode) => (
    <Popover
      open={open === field}
      onOpenChange={(next) => setOpen(next ? field : null)}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      content={content}
      rootClassName="v2-field-dropdown"
    >
      {children}
    </Popover>
  );

  const departureLabel = labelForValue(departureGroups, draft.exEnd[0] || "");

  return (
    <div className="v2-listing__searchbar">
      <div className="v2-search-fields">
        {dropdown(
          "keyword",
          <div className="v2-field-dropdown__list">
            <PickerList
              groups={destinationGroups}
              rowIcon={<PinIcon size={16} />}
              value={draft.keyword}
              query={keywordText}
              onSelect={(option) => {
                setDraft({ ...draft, keyword: option.value, locationId: null });
                setKeywordText(option.label);
                setOpen(null);
              }}
            />
          </div>,
          <div className="v2-search-field">
            <PinIcon size={20} />
            <input
              className="v2-search-field__input"
              value={keywordText}
              placeholder="Where would you like to go?"
              onChange={(event) => {
                setKeywordText(event.target.value);
                setDraft({ ...draft, keyword: event.target.value, locationId: null });
                setOpen("keyword");
              }}
            />
          </div>
        )}

        {filters.type !== "cruise" &&
          dropdown(
            "exEnd",
            <div className="v2-field-dropdown__list">
              <PickerList
                groups={departureGroups}
                rowIcon={<Icon path={mdiAirplane} size="16px" />}
                value={draft.exEnd[0] || ""}
                onSelect={(option) => {
                  setDraft({ ...draft, exEnd: [option.value] });
                  setOpen(null);
                }}
              />
            </div>,
            <button type="button" className="v2-search-field">
              <HomeIcon size={20} />
              <span className={`v2-search-field__value ${departureLabel ? "" : "is-empty"}`}>
                {departureLabel || "Departure City"}
              </span>
            </button>
          )}

        {dropdown(
          "date",
          <div className="v2-field-dropdown__months">
            <MonthGrid
              year={dateYear}
              onYearChange={setDateYear}
              month={draft.date && dayjs(draft.date).year() === dateYear ? dayjs(draft.date).month() : null}
              onMonthChange={(month) => {
                setDraft({
                  ...draft,
                  date: month === null ? null : dayjs().year(dateYear).month(month).format("YYYY-MM-01"),
                });
                if (month !== null) setOpen(null);
              }}
            />
          </div>,
          <button type="button" className="v2-search-field">
            <CalendarIcon size={20} />
            <span className={`v2-search-field__value ${draft.date ? "" : "is-empty"}`}>
              {draft.date ? dayjs(draft.date).format(monthFormat) : "Travel Date"}
            </span>
          </button>
        )}

        {dropdown(
          "brands",
          <BrandDropdown
            brands={brands}
            value={draft.brands}
            onCancel={() => setOpen(null)}
            onApply={(next) => {
              setDraft({ ...draft, brands: next });
              setOpen(null);
            }}
          />,
          <button type="button" className="v2-search-field v2-search-field--brands">
            <BrandIcon size={20} />
            {brandTags() || <span className="v2-search-field__value is-empty">Brand</span>}
          </button>
        )}
      </div>

      <Button type="primary" className="v2-listing__search-btn" onClick={() => onSearch(draft)}>
        Search Holidays
      </Button>
    </div>
  );
}
